import { Box } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import Button from "../../../design/Button";
import colors from "../../../design/colors";
import styles from "../../../design/styles";
import customStyles from "./styles";

const RESEND_COOLDOWN_SECONDS = 20;

const VerifyOTP = ({
  phoneNumber,
  onVerify,
  onCancel,
  onResend,
  isVerifying,
  error,
}) => {
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    startResendTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startResendTimer = () => {
    setResendTimer(RESEND_COOLDOWN_SECONDS);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendTimer > 0 || isResending) return;
    setIsResending(true);
    try {
      await onResend();
      startResendTimer();
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) setOtp(value);
  };

  const handleVerify = () => {
    if (!otp || otp.length < 4) return;
    onVerify(otp);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleVerify();
    e.stopPropagation();
  };

  const canVerify = otp.length >= 4 && !isVerifying;

  return (
    <Box
      sx={customStyles.container}
    >
      {/* Content */}
      <Box
        sx={customStyles.numberNotVerifiedContent}
      >
        <p
          className="t6 medium-font nc-gray-900"
          style={customStyles.pl1}
        >
          Enter verification code
        </p>
        <p
          className="t7 regular-font nc-gray-700"
          style={customStyles.pl2}
        >
          Please enter the verification code sent to {phoneNumber}
        </p>

        {/* OTP Input */}
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="Enter code"
          value={otp}
          onChange={handleOtpChange}
          onKeyDown={handleKeyDown}
          autoFocus
          className="regular-font t6 nc-gray-900"
          style={{
            border: `1px solid ${error ? colors.ic_red_300 : colors.nc_gray_400}`,
            ...customStyles.otpInput
          }}
        />

        {/* Error message */}
        {error && (
          <p
            className="t8 regular-font"
            style={{ margin: 0, color: colors.ic_red_300 }}
          >
            {error}
          </p>
        )}

        {/* Resend */}
        <div style={{ margin: 0 }}>
          {resendTimer > 0 ? (
            <p
              className="t7 regular-font nc-gray-600"
              style={customStyles.pl2}
            >
              Resend code{" "}
              <span className="medium-font" style={{ fontStyle: "italic" }}>
                in {resendTimer}s
              </span>
            </p>
          ) : (
            <p
              className="t7 regular-font"
              style={customStyles.pl2}
            >
              Didn't receive SMS?
              <span
                className="medium-font"
                style={{
                  color: colors.ic_green_400,
                  cursor: isResending ? "not-allowed" : "pointer",
                }}
                onClick={handleResend}
              >
                {" "}Send again
              </span>
            </p>
          )}
        </div>
      </Box>

      {/* Actions */}
      <Box
        sx={customStyles.actionsContainer}
      >
        <Button
          hierarchy="white"
          variant="outlined"
          buttonText="Cancel"
          disabled={isVerifying}
          onClick={onCancel}
          styleOverride={{
            height: "36px",
            padding: "8px 16px",
            ...styles.t7,
          }}
        />
        <Button
          hierarchy="green"
          variant="contained"
          buttonText="Verify"
          isLoading={isVerifying}
          disabled={!canVerify}
          onClick={handleVerify}
          styleOverride={{
            height: "36px",
            padding: "8px 16px",
            ...styles.t7,
            color: canVerify ? colors.ic_white : colors.nc_gray_400,
          }}
        />
      </Box>
    </Box>
  );
};

export default VerifyOTP;
